import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { Box, Grid, Paper, Button, Divider, Typography, IconButton } from '@material-ui/core';
import { DnDtable } from 'components/UI';
import { withRouter } from 'react-router-dom';
import EditIcon from '@material-ui/icons/Edit';
import { parseRulesHours } from 'services/rules';
import useStyles from './BusinessHoursTable.styled';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/AddCircle';
import { defaultBusinessHoursData } from 'services/rules';
import { BusinessHoursRulesModal } from 'components/Modals';

const BusinessHoursTable = ({ rules, action, bh = [], onDelete, onSubmitHours }) => {
  const classes = useStyles();
  const [businessHours, setBusinessHours] = useState(null);
  const [openBusinessHoursRulesModal, setOpenBusinessHoursRulesModal] = useState(false);
  const [columns, setColumns] = useState([
    {
      name: '',
      style: { width: '0%' },
    },
    {
      name: i18n.t('business_hours.day'),
      selector: 'day',
      style: { width: '40%', textTransform: 'capitalize' },
      cell: (row) => (
        <Typography>
          <Box component="span" fontSize={12}>
            {i18n.t(`common.${row.day}`)}
          </Box>
        </Typography>
      ),
    },
    {
      name: `${i18n.t('business_hours.from')} - ${i18n.t('business_hours.to')}`,
      selector: 'from',
      style: { width: '40%' },
      cell: (row) => (
        <Box display="flex" flexDirection="column">
          {row?.closed ? (
            <Typography>
              <Box component="span" fontSize={12}>
                {i18n.t('business_hours.closed')}
              </Box>
            </Typography>
          ) : row?.fullDay ? (
            <Typography>
              <Box component="span" fontSize={12}>
                {i18n.t('business_hours.open')}
              </Box>
            </Typography>
          ) : (
            <>
              {row.hours.map((hours, i) => (
                <Typography key={i}>
                  <Box component="span" fontSize={12}>
                    {parseRulesHours(hours.start)} - {parseRulesHours(hours.end)}
                  </Box>
                </Typography>
              ))}
            </>
          )}
        </Box>
      ),
    },
  ]);

  /**
   * Handle open/close rules modal
   */
  const handleOpenBusinessHoursRulesModal = () => {
    setOpenBusinessHoursRulesModal(!openBusinessHoursRulesModal);
  };

  useEffect(() => {
    if (bh.length > 0) {
      setBusinessHours(defaultBusinessHoursData(bh));
    }
  }, [bh]);

  useEffect(() => {
    if (action !== 'details') {
      setColumns((prevValue) => [
        ...prevValue,
        {
          name: i18n.t('common.actions'),
          align: 'center',
          selector: 'action',
          cell: (row) => {
            return (
              <Box display="flex" justifyContent="center">
                <IconButton
                  aria-label="edit"
                  size="medium"
                  onClick={() => {
                    handleOpenBusinessHoursRulesModal();
                  }}
                >
                  <EditIcon fontSize="inherit" />
                </IconButton>
                <IconButton
                  size="medium"
                  aria-label="delete"
                  disabled={row.closed}
                  onClick={() => onDelete(row)}
                >
                  <DeleteIcon fontSize="inherit" />
                </IconButton>
              </Box>
            );
          },
        },
      ]);
    }
  }, [action]); // eslint-disable-line

  const handleBHSubmit = (bh) => {
    onSubmitHours(bh);
  };

  return (
    <Grid item xs={12}>
      <Paper elevation={1} className={classes.paper}>
        <DnDtable columns={columns} editable={false} data={businessHours} />
        {action !== 'details' && (
          <>
            <Divider />
            <Grid item xs={12}>
              <Grid
                container
                spacing={1}
                direction="row"
                alignItems="center"
                justifyContent="flex-end"
              >
                <Grid item>
                  <Box display="flex" justifyContent="center" fontSize={13}>
                    <Button
                      data-cy="add-rule-button"
                      color="primary"
                      fontSize={13}
                      style={{
                        textTransform: 'capitalize',
                        marginRight: 15,
                      }}
                      onClick={() => {
                        handleOpenBusinessHoursRulesModal();
                      }}
                      startIcon={<AddIcon />}
                    >
                      {i18n.t('ruleset.add_rule')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
      {openBusinessHoursRulesModal && (
        <BusinessHoursRulesModal
          rules={rules}
          onSubmitHours={handleBHSubmit}
          open={openBusinessHoursRulesModal}
          handleClick={handleOpenBusinessHoursRulesModal}
        />
      )}
    </Grid>
  );
};

BusinessHoursTable.propTypes = {
  rules: PropTypes.array,
  action: PropTypes.string,
  bh: PropTypes.array,
  onDelete: PropTypes.func,
  onSubmitHours: PropTypes.func,
};

export default withRouter(BusinessHoursTable);
